import { JobPipelineStepDetail, PipelineStepEnumSchema, StatusEnumSchema } from "@domain/schemas/BI";
import { DropdownFilterCell } from "@presentation/components/DropdownFilterCell";
import { DataResult, process } from "@progress/kendo-data-query";
import {
  GridDetailRowProps,
  GRID_PREVENT_SELECTION_ELEMENT,
  GridDataStateChangeEvent,
  getSelectedState,
  GridSelectionChangeEvent,
  GridColumn,
  Grid,
  GridRowClickEvent,
  GridExpandChangeEvent,
} from "@progress/kendo-react-grid";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Switch } from "@progress/kendo-react-inputs"; // Added for subtle toggle
import React, { useContext, useEffect, useState } from "react";
import { DATA_ITEM_KEY, EXPANDED_FIELD, SELECTED_FIELD } from "@presentation/components/helpers/constants";
import { BatchArtifactsViewerContext, DropdownFilterFocusContext } from "../JobList";
import { BatchListItemState, DetailDataGridStore } from "@presentation/common";
import { StepListItemState } from "@presentation/common/types/extensions/StepListItemState";
import { createDetailDataGridStore } from "@presentation/common/stores/DetailDataGridStore";
import { BatchesView } from "@presentation/components/job/components/BatchesView";
import { useInjection } from "@presentation/components/dependencyInjection/useInjection";
import { DownloadResultsService, ListJobsService } from "@application/useCases";
import "@presentation/components/job/styles/progressBarStyle.scss";
import { formatStepName } from "../JobList";
import { useTranslation } from "react-i18next";
import StatusCell from "./StatusCell";
import ShowLogsButton from "./ShowLogsButton";
import { Button } from "@progress/kendo-react-buttons";
import { downloadIcon, fileIcon } from "@progress/kendo-svg-icons";
import { subscribeToJob, subscribeToRefreshJob } from "@infrastructure/realtime/jobsHubClient";
import { useArtifactDownloadDialog } from "./useArtifactDownloadDialog";
import { useShallow } from "zustand/react/shallow";

const useStepsGridStore = createDetailDataGridStore<StepListItemState, JobPipelineStepDetail>();

/**
 * Helper to format milliseconds into HH:mm:ss
 */
const formatDuration = (ms: number) => {
  if (ms <= 0 || Number.isNaN(ms)) return "00:00:00";
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return [hours, minutes, seconds].map((v) => (v < 10 ? "0" + v : v)).join(":");
};

const STEP_ORDER = [
  PipelineStepEnumSchema.DownloadData,
  PipelineStepEnumSchema.PulsarStep1,
  PipelineStepEnumSchema.PulsarStep2,
  PipelineStepEnumSchema.PulsarStep3,
  PipelineStepEnumSchema.LibraryGeneration,
  PipelineStepEnumSchema.DiaAnalysis,
  PipelineStepEnumSchema.SneCombine,
  PipelineStepEnumSchema.SneMerge,
  // SpectroMine merge step: always the last stage of the SpectroMine pipeline.
  PipelineStepEnumSchema.PsarMerging,
];

// Unknown/unlisted steps must sort last instead of first (indexOf returns -1).
const stepOrderIndex = (type: JobPipelineStepDetail["type"]) => {
  const index = STEP_ORDER.indexOf(type);
  return index === -1 ? STEP_ORDER.length : index;
};

export const StepsView = (properties: GridDetailRowProps) => {
  const { isDropdownFilterFocusedRef } = useContext(DropdownFilterFocusContext);
  const { openLogs, openResults } = useContext(BatchArtifactsViewerContext);
  const [showCumulative, setShowCumulative] = useState(false);

  const handleDropdownToggle = (isFocused: boolean) => {
    isDropdownFilterFocusedRef.current = isFocused;
  };

  const jobId: string = properties.dataItem.id;

  const { detailList, metaState, selectedState, setDetailList, setIsExpanded, setMetaStateForDetail, setSelectedStateForDetail } = useStepsGridStore(
    useShallow((state: DetailDataGridStore<StepListItemState, JobPipelineStepDetail>) => ({
      detailList: state.detailDictionary[jobId]?.detailList,
      metaState: state.detailDictionary[jobId]?.metaState,
      selectedState: state.detailDictionary[jobId]?.selectedState,
      setDetailList: state.setDetailList,
      setIsExpanded: state.setIsExpanded,
      setMetaStateForDetail: state.setMetaStateForDetail,
      setSelectedStateForDetail: state.setSelectedStateForDetail,
    })),
  );

  const listJobsTasksService = useInjection(ListJobsService);
  const downloadResultsService = useInjection(DownloadResultsService);
  const [dataResult, setDataResult] = useState<DataResult>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedJobStep, setselectedJobStep] = useState<StepListItemState | null>(null);
  const [isDoubleClickDialogVisible, setIsDoubleClickDialogVisible] = useState(false);

  const { t: loc } = useTranslation();

  const handleRowDoubleClick = (event: GridRowClickEvent) => {
    if (event.dataItem.isInlineBatchStep) return;
    setselectedJobStep(event.dataItem);
    setIsDoubleClickDialogVisible(true);
  };

  const closeDialog = () => {
    setIsDoubleClickDialogVisible(false);
    setselectedJobStep(null);
  };

  const { artifactDownloadDialog, openStepDownloadDialog } = useArtifactDownloadDialog({ downloadResultsService });

  async function handleStepDownload(stepId: string) {
    try {
      await openStepDownloadDialog(jobId, stepId);
    } catch {
      alert("Failed to download step artifacts.");
    }
  }

  useEffect(() => {
    const fetchJobStepsList = async () => {
      if (isDropdownFilterFocusedRef.current) return null;

      const jobPipelineStepListResponse: JobPipelineStepDetail[] = await listJobsTasksService.getJobStepListAsync(jobId);

      const sortedBase = jobPipelineStepListResponse
        .toSorted((a, b) => stepOrderIndex(a.type) - stepOrderIndex(b.type))
        .filter((jobStep) => {
          const isLooksLikeSkippedStep = jobStep.status === StatusEnumSchema.Completed && !jobStep.startedAt && !jobStep.finishedAt;

          return !isLooksLikeSkippedStep;
        });

      const jobStepList = await Promise.all(
        sortedBase.map(async (jobStep, index) => {
          const startedAt = jobStep.startedAt ? new Date(jobStep.startedAt) : null;
          const finishedAt = jobStep.finishedAt ? new Date(jobStep.finishedAt) : null;
          // eslint-disable-next-line no-useless-assignment -- fallback value used if the batch fetch below throws
          let batchList: BatchListItemState[] = [];

          let stepDurationMs = 0;
          if (startedAt) {
            const end = finishedAt ? finishedAt.getTime() : Date.now();
            stepDurationMs = Math.max(0, end - startedAt.getTime());
          }

          let cumulativeMs = 0;
          const stepsSoFar = sortedBase.slice(0, index + 1);
          for (const currentStep of stepsSoFar) {
            if (!currentStep.startedAt) continue;

            const currentStartedAt = new Date(currentStep.startedAt);
            const currentFinishedAt = currentStep.finishedAt ? new Date(currentStep.finishedAt) : new Date();
            cumulativeMs += Math.max(0, currentFinishedAt.getTime() - currentStartedAt.getTime());
          }

          let singleBatch = null;
          let isInlineBatchStep = false;
          try {
            const batchResponse = await listJobsTasksService.getJobStepBatchListAsync(jobId, jobStep.id);
            batchList = (batchResponse.list ?? []) as BatchListItemState[];
            isInlineBatchStep = !jobStep.isParallel && batchList.length === 1;
            singleBatch = isInlineBatchStep ? batchList[0] : null;
          } catch (error) {
            console.error(`Error fetching batch for step ${jobStep.id}:`, error);
          }

          return {
            ...jobStep,
            step: formatStepName(jobStep.type, properties.dataItem.software),
            jobId: jobId,
            virtualCreatedAt: jobStep.createdAt ? new Date(jobStep.createdAt) : null,
            virtualStartedAt: startedAt,
            virtualFinishedAt: finishedAt,
            durationDisplay: formatDuration(stepDurationMs),
            cumulativeDisplay: formatDuration(cumulativeMs),
            runningBatchesInfo: `${jobStep.runningBatchesCount} / ${jobStep.batchesCount}`,
            completedBatchesInfo: `${jobStep.completedBatchesCount} / ${jobStep.batchesCount}`,
            failedBatchesInfo: `${jobStep.failedBatchesCount ?? (jobStep.batchesCount ?? 0) - (jobStep.completedBatchesCount ?? 0) - (jobStep.runningBatchesCount ?? 0)} / ${jobStep.batchesCount ?? 0}`,
            batchId: singleBatch?.id,
            batchPrecursorsCount: (singleBatch as BatchListItemState | null)?.precursorsCount ?? null,
            isInlineBatchStep,
          };
        }),
      );

      setDetailList(jobId, jobStepList);
      return jobStepList;
    };

    void fetchJobStepsList();

    const unsubscribeFromJob = subscribeToJob(jobId);
    const unsubscribeFromRefreshJob = subscribeToRefreshJob((refreshedJobId) => {
      if (refreshedJobId === jobId) {
        void fetchJobStepsList();
      }
    });

    return () => {
      unsubscribeFromRefreshJob();
      unsubscribeFromJob();
    };
  }, [jobId]);

  useEffect(() => {
    if (!(detailList && metaState)) {
      return;
    }

    setDataResult(process(detailList, metaState));
    setIsLoading(false);
  }, [metaState, detailList]);

  const handleSelectionChange = (event: GridSelectionChangeEvent) => {
    const newSelectedState = getSelectedState({
      event,
      selectedState: selectedState,
      dataItemKey: DATA_ITEM_KEY,
    });
    setSelectedStateForDetail(jobId, newSelectedState);
  };

  const handleDataStateChange = (e: GridDataStateChangeEvent) => {
    setMetaStateForDetail(jobId, e.dataState);
    setDataResult(process(detailList, e.dataState));
  };

  const handleExpandChange = (event: GridExpandChangeEvent) => {
    if (event.dataItem.isInlineBatchStep) {
      event.dataItem.expanded = false;
      return;
    }

    event.dataItem.expanded = event.value;
    const expandedJobSteps: StepListItemState = event.dataItem;

    if (!expandedJobSteps.id) return;
    setIsExpanded(jobId, event.dataItem.id, event.value);
  };

  const rowRender = (trElement: React.ReactElement<HTMLTableRowElement>, properties_: { dataItem: StepListItemState }) => {
    const className = properties_.dataItem.isInlineBatchStep ? `${trElement.props.className ?? ""} inline-step-no-expand`.trim() : trElement.props.className;

    return React.cloneElement(trElement, {
      ...trElement.props,
      className,
    });
  };

  if (detailList && !isLoading) {
    return (
      <div {...{ [GRID_PREVENT_SELECTION_ELEMENT]: false }}>
        {artifactDownloadDialog}
        <style>
          {`.inline-step-no-expand .k-hierarchy-cell .k-button { visibility: hidden; pointer-events: none; }
            .inline-step-no-expand .k-hierarchy-cell .k-icon,
            .inline-step-no-expand .k-hierarchy-cell .k-svg-icon { display: none; }`}
        </style>
        {/* Subtle Toggle Switch */}
        <div style={{ marginBottom: "8px", display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "8px" }}>
          <span style={{ fontSize: "12px", color: "#656565" }}>Cumulative duration View</span>
          <Switch size="small" checked={showCumulative} onChange={(e) => setShowCumulative(e.value)} />
        </div>

        <Grid
          style={{ maxHeight: "80vh", width: "100%" }}
          dataItemKey={DATA_ITEM_KEY}
          selectedField={SELECTED_FIELD}
          expandField={EXPANDED_FIELD}
          scrollable="scrollable"
          resizable={true}
          sortable={true}
          filterable={false}
          navigatable={false}
          selectable={{
            enabled: true,
            drag: false,
            mode: "multiple",
          }}
          {...metaState}
          data={dataResult}
          detail={(detailProperties) => (detailProperties.dataItem.isInlineBatchStep ? null : <BatchesView {...detailProperties} />)}
          onExpandChange={handleExpandChange}
          onRowDoubleClick={handleRowDoubleClick}
          onDataStateChange={handleDataStateChange}
          onSelectionChange={handleSelectionChange}
          rowRender={rowRender}
        >
          <GridColumn field="step" title="Step" width="200px" minResizableWidth={150} sortable={false} />
          <GridColumn
            field="status"
            title={loc("status")}
            width="220px"
            minResizableWidth={100}
            cell={(properties_) => (
              <StatusCell status={properties_.dataItem.status} progress={properties_.dataItem.progress} isbatch={false} currentStep="" failedBatchesCount={properties_.dataItem.failedBatchesCount} />
            )}
            filterCell={(properties_) => <DropdownFilterCell {...properties_} data={Object.values(StatusEnumSchema) as string[]} defaultItem={"Select status"} onToggle={handleDropdownToggle} />}
          />

          {properties.dataItem.status === StatusEnumSchema.Running && <GridColumn field="runningBatchesInfo" title="Running" width="150px" />}
          <GridColumn field="completedBatchesInfo" title="Completed" width="150px" />
          <GridColumn
            title={loc("logs")}
            width="140px"
            minResizableWidth={120}
            filterable={false}
            cell={(properties) => (
              <td style={{ position: "relative", overflow: "visible" }}>
                {properties.dataItem.isInlineBatchStep &&
                  properties.dataItem.batchId &&
                  properties.dataItem.status !== StatusEnumSchema.Created &&
                  properties.dataItem.status !== StatusEnumSchema.Pending &&
                  properties.dataItem.status !== StatusEnumSchema.Scheduled && (
                    <ShowLogsButton
                      onMouseUp={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        void openLogs(jobId, properties.dataItem.id, { id: properties.dataItem.batchId, status: properties.dataItem.status });
                      }}
                      aria-label="Show Logs"
                    />
                  )}
              </td>
            )}
          />
          <GridColumn
            title={"Artifacts"}
            width="150px"
            minResizableWidth={130}
            filterable={false}
            cell={(properties) => (
              <td style={{ textAlign: "left" }}>
                {properties.dataItem.status === StatusEnumSchema.Completed && (
                  <Button
                    themeColor="secondary"
                    fillMode="link"
                    style={{ padding: 0 }}
                    svgIcon={downloadIcon}
                    onMouseUp={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleStepDownload(properties.dataItem.id);
                    }}
                  >
                    Download
                  </Button>
                )}
              </td>
            )}
          />
          <GridColumn
            title={"Results Overview"}
            width="170px"
            minResizableWidth={160}
            filterable={false}
            cell={(properties) => (
              <td style={{ textAlign: "left" }}>
                {properties.dataItem.isInlineBatchStep && properties.dataItem.batchId && properties.dataItem.status === StatusEnumSchema.Completed && properties.dataItem.name !== "DownloadData" && (
                  <Button
                    themeColor="secondary"
                    fillMode="link"
                    style={{ padding: 0 }}
                    svgIcon={fileIcon}
                    onMouseUp={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      void openResults(jobId, properties.dataItem.id, { id: properties.dataItem.batchId, status: properties.dataItem.status });
                    }}
                  >
                    {properties.dataItem.batchPrecursorsCount ?? "Results"}
                  </Button>
                )}
              </td>
            )}
          />
          <GridColumn field={showCumulative ? "cumulativeDisplay" : "durationDisplay"} title={showCumulative ? "Cumulative" : "Duration"} width="140px" />
          <GridColumn field="virtualStartedAt" title={loc("startedAt")} width="220px" filter="date" format="{0:dd/MM/yyyy h:mm a}" />
          <GridColumn field="virtualFinishedAt" title={loc("finishedAtNoTime")} width="220px" filter="date" format="{0:dd/MM/yyyy h:mm a}" />
          <GridColumn field="batchId" title={loc("batch")} width="220px" />

          <GridColumn width="auto" minResizableWidth={0} filterable={false} />
        </Grid>

        {isDoubleClickDialogVisible && selectedJobStep && !selectedJobStep.isInlineBatchStep && (
          <Dialog title={`${selectedJobStep.type} batches`} onClose={closeDialog} style={{ overflowX: "auto" }}>
            <div>
              <BatchesView dataItem={selectedJobStep} dataIndex={0} />
            </div>
          </Dialog>
        )}
      </div>
    );
  }

  return (
    <div style={{ height: "50px", width: "100%" }}>
      <div style={{ position: "absolute", width: "100%" }}>
        <div className="k-loading-image" />
      </div>
    </div>
  );
};
